import { Injectable } from '@angular/core';
import {
  AirportDropDown,
  AirportDropDownList,
  CityData,
  SearchableCity
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { Search } from '@com-delta-omnichannel/mach-core-js';
import { FLIGHT_STATUS_SMART_CONSTANTS } from '../models/constants/flight-status.constants';

@Injectable({
  providedIn: 'root'
})
export class FlightStatusContainerHelperService {
  initializeAirportModalContent(): AirportDropDown {
    return {
      airportList: {
        id: 'flight-status-search-transparent',
        title: FLIGHT_STATUS_SMART_CONSTANTS.LABELS.DESTINATION,
        placeholder: FLIGHT_STATUS_SMART_CONSTANTS.PLACEHOLDERS.SEARCH_AIRPORTS,
        hasExternalFiltering: false,
        shouldAutoCapitalize: true,
        items: [],
        menuVariant: FLIGHT_STATUS_SMART_CONSTANTS.CONFIG.SEARCH_MENU_TYPE,
        hasAutocomplete: false,
        errorConfig: {
          validateOnInput: false
        }
      },
      tabOptions: {
        id: 'tabs-width-fixed-small',
        tabVariant: 'standard',
        tabSize: 'small',
        tabWidth: 'fixed',
        tabItems: [
          {
            id: 'search-tab-display',
            label: 'Search',
            value: 'search-tab-display',
            selected: true
          }
        ],
        selectedIndex: 0
      },
      mobileCloseHeaderText:
        FLIGHT_STATUS_SMART_CONSTANTS.UI_TEXT.SELECT_AIRPORT,
      floatingLabelText:
        FLIGHT_STATUS_SMART_CONSTANTS.FLOATING_LABELS.SEARCH_AIRPORTS
    };
  }

  updateSearchResults(cities: CityData[]): SearchableCity[] {
    return cities.map((city) => ({
      ...city,
      id: city.id,
      label: `${city.id} - ${city.label}, ${city.country || ''}`,
      code: city.id,
      text: city.id.toLowerCase(),
      selected: false,
      value: city.id
    }));
  }

  rebuildSearchComponent(
    airportModalContent: AirportDropDown,
    searchForAirportFn: (term: string) => void,
    onAirportSelectedFn: (airport: CityData) => void
  ): Search | null {
    const searchElement = document.getElementById(
      FLIGHT_STATUS_SMART_CONSTANTS.IDS.AIRPORT_SEARCH
    );
    if (!searchElement) return null;

    const inputElement = searchElement.querySelector(
      FLIGHT_STATUS_SMART_CONSTANTS.SELECTORS.INPUT
    ) as HTMLInputElement;
    const inputValue = (inputElement && inputElement.value) || '';

    searchElement.innerHTML = '';
    const searchInstance = new Search(airportModalContent.airportList);
    searchInstance.onSearch(searchForAirportFn);
    searchInstance.onSelect(onAirportSelectedFn);
    setTimeout(() => {
      const newInput = searchElement.querySelector(
        FLIGHT_STATUS_SMART_CONSTANTS.SELECTORS.INPUT
      ) as HTMLInputElement;
      if (newInput && inputValue) {
        newInput.value = inputValue;
        newInput.focus();
        newInput.dispatchEvent(
          new Event(FLIGHT_STATUS_SMART_CONSTANTS.EVENTS.INPUT, {
            bubbles: true
          })
        );
      }
    }, 10);

    return searchInstance;
  }

  initializeSearchComponent(
    airportModalContent: AirportDropDown,
    searchForAirportFn: (term: string) => void,
    onAirportSelectedFn: (airport: CityData) => void
  ): Search | null {
    const searchElement = document.getElementById(
      FLIGHT_STATUS_SMART_CONSTANTS.IDS.AIRPORT_SEARCH
    );
    if (!searchElement || !airportModalContent) return null;

    const searchInstance = new Search(airportModalContent.airportList);
    searchInstance.onSearch(searchForAirportFn);
    searchInstance.onSelect(onAirportSelectedFn);
    return searchInstance;
  }

  private getCodeValue(city: CityData): string {
    return city.id || city.code || '';
  }

  private getNameValue(city: CityData): string {
    return city.label || city.name || '';
  }

  convertCityDataToAirportDropDown(city: CityData): AirportDropDownList {
    return {
      code: this.getCodeValue(city),
      name: this.getNameValue(city)
    };
  }

  closeModal(): void {
    setTimeout(() => {
      const closeButton = document.querySelector(
        FLIGHT_STATUS_SMART_CONSTANTS.SELECTORS.CLOSE_BUTTON
      ) as HTMLElement | null;
      if (closeButton) closeButton.click();
    }, 50);
  }
}
